import {
  CancellationDueDeath,
  ChangeControlDataForm,
  DeathDataForm,
} from 'cancellations/view-models/cancellation-due-death';
import { DateTime } from 'luxon';
import { ActType } from 'shell/constants/act-type';
import { idActRegex } from 'shell/constants/regex';
import { Generality } from 'shell/models/person/generality';
import { DocumentDigital } from 'shell/view-models/act-form';
import * as Yup from 'yup';

const changeControlDataFormSchema = Yup.object().shape({
  effectiveDate: Yup.date()
    .nullable()
    .required('Campo obbligatorio')
    .max(
      DateTime.local().plus({ days: 1 }),
      'La data di decorrenza non può essere successiva alla data odierna'
    )
    .typeError('Data non valida'),
  cancellationReason: Yup.string().nullable().required('Il campo è richiesto'),
});

const deathDataFormSchema = Yup.object()
  .shape({
    eventDate: Yup.object().shape({
      date: Yup.date()
        .nullable()
        .required('Campo obbligatorio')
        .max(
          DateTime.local().plus({ days: 1 }),
          'La data evento non può essere successiva alla data odierna'
        )
        .typeError('Data non valida'),
    }),
    happenInItaly: Yup.boolean(),
    exceptionalPlace: Yup.string(),
    actType: Yup.string(),
    digital: Yup.object()
      .nullable()
      .when('actType', {
        is: ActType.AttoDigitale,
        otherwise: Yup.object().nullable(),
        then: Yup.object()
          .nullable()
          .shape({
            idAnsc: Yup.string()
              .nullable()
              .matches(idActRegex, 'Formato non corretto')
              .test((value) => {
                if (value) {
                  const [year] = value.split('-');
                  const isValid = year ? Number.parseInt(year) >= 2022 : true;

                  return isValid
                    ? true
                    : new Yup.ValidationError(
                        "L'idAnsc deve avere anno maggiore al 2022",
                        undefined,
                        'deathDataForm.digital.idAnsc'
                      );
                }
                return true;
              })
              .required('Campo richiesto'),
            documentDate: Yup.date()
              .nullable()
              .notRequired()
              .typeError('Formato non valido')
              .required('Campo richiesto'),
            municipality: Yup.object().shape({}).nullable().required('Campo richiesto'),
          })
          .test((values) => {
            const castedValues = values as unknown as DocumentDigital;

            if (
              castedValues?.municipality?.id &&
              castedValues?.istatCode &&
              castedValues?.municipality?.id === castedValues.istatCode
            ) {
              return true;
            }
            return new Yup.ValidationError([
              new Yup.ValidationError(
                'Non coincidono i due codici ISTAT',
                undefined,
                'deathDataForm.digital.municipality'
              ),
              new Yup.ValidationError(
                'Non coincidono i due codici ISTAT',
                undefined,
                'deathDataForm.digital.istatCode'
              ),
            ]);
          }),
      }),
    paper: Yup.object()
      .nullable()
      .when('actType', {
        is: ActType.AttoCartaceo,
        otherwise: Yup.object().nullable(),
        then: Yup.object().shape({
          actDate: Yup.date()
            .nullable()
            .max(
              DateTime.local().plus({ days: 1 }),
              'La data non può essere successiva alla data odierna'
            )
            .typeError('Data non valida'),
          transcribed: Yup.string().nullable(),
          actMunicipality: Yup.object().nullable().shape({}).required('Il campo è richiesto'),
          actYear: Yup.number()
            .nullable()
            .required('Campo richiesto')
            .typeError('Sono ammesse solamente cifre'),
          actNumber: Yup.number()
            .nullable()
            .required('Campo richiesto')
            .typeError('Sono ammesse solamente cifre'),
          actPart: Yup.string()
            .nullable()
            .required('Campo richiesto')
            .test(
              'part-ammisible-values',
              'Gli unici valori ammessi sono 1 o 2',
              (value) => ['1', '2'].indexOf(value || '') >= 0
            ),
          actSeries: Yup.string().nullable(),
          // .required('Campo richiesto')
          // .test(
          //   'part-ammisible-values',
          //   'Gli unici valori ammessi sono A, B o C',
          //   (value) => ['A', 'B', 'C'].indexOf(value || '') >= 0
          // ),
          actVolume: Yup.string()
            .nullable()
            .when('actMunicipality.actMunicipalityDescription', {
              is: (description: string) => (description || '').toLocaleLowerCase() === 'milano',
              then: Yup.string()
                .required('Campo obbligatorio')
                .matches(/^R[0-9]{2}$/, {
                  message: 'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                }),
            }),
        }),
      }),
    office: Yup.string().nullable().notRequired(),
    eventItaly: Yup.object().when('happenInItaly', {
      is: (happenInItaly: boolean) => happenInItaly,
      then: Yup.object().shape({}).nullable().notRequired(),
      otherwise: Yup.object()
        .nullable()
        .shape({
          municipalityId: Yup.string().nullable().required('Campo richiesto'),
          municipalityDescription: Yup.string().nullable().required('Campo richiesto'),
          province: Yup.string().nullable().required('Campo richiesto'),
          provinceCode: Yup.string().nullable().required('Campo richiesto'),
          location: Yup.string().nullable(),
        })
        .required('Campo obbligatorio'),
    }),
    eventForeign: Yup.object().when('happenInItaly', {
      is: (happenInItaly: boolean) => !happenInItaly,
      then: Yup.object().shape({}).nullable().notRequired(),
      otherwise: Yup.object()
        .nullable()
        .shape({
          stateId: Yup.string().nullable().required('Campo richiesto'),
          stateDescription: Yup.string().nullable().required('Campo richiesto'),
          municipalityId: Yup.string().nullable().required('Campo richiesto'),
          municipalityDescription: Yup.string().nullable().required('Campo richiesto'),
          // province: Yup.string().nullable().required('Campo richiesto'),
        })
        .required('Campo obbligatorio'),
    }),
  })
  .test((values) => {
    const castedValues = values as unknown as DeathDataForm;
    const errors = [];

    if (
      castedValues.actType === ActType.AttoDigitale &&
      castedValues.eventDate?.date &&
      castedValues.digital?.documentDate &&
      castedValues.eventDate?.date > castedValues.digital?.documentDate
    ) {
      errors.push(
        new Yup.ValidationError(
          "La data dell'evento non può essere successiva alla data del documento",
          undefined,
          'deathDataForm.digital.documentDate'
        )
      );
    }

    if (
      castedValues.actType === ActType.AttoCartaceo &&
      castedValues.eventDate?.date &&
      castedValues.paper?.actDate &&
      castedValues.eventDate?.date > castedValues.paper?.actDate
    ) {
      errors.push(
        new Yup.ValidationError(
          'La data non può essere minore della data di decesso',
          undefined,
          'deathDataForm.paper.actDate'
        )
      );
    }

    if (
      castedValues.actType === ActType.AttoCartaceo &&
      castedValues.eventDate?.date &&
      castedValues.paper?.actYear &&
      DateTime.fromJSDate(castedValues.eventDate?.date as unknown as Date)?.get('year') >
        Number.parseInt(castedValues.paper?.actYear, 10)
    ) {
      errors.push(
        new Yup.ValidationError(
          "L'anno deve essere successivo all'anno di decesso",
          undefined,
          'deathDataForm.paper.actYear'
        )
      );
    }

    if (errors.length > 0) {
      return new Yup.ValidationError(errors);
    }

    return true;
  });

const judgmentDataFormSchema = Yup.object().shape({
  judgmentNumber: Yup.string().nullable().notRequired(),
  judgmentDate: Yup.date()
    .nullable()
    .required('Campo obbligatorio')
    .max(DateTime.local().plus({ days: 1 }), 'La data non può essere successiva alla data odierna')
    .typeError('Data non valida'),
  validityDate: Yup.date()
    .nullable()
    .required('Campo obbligatorio')
    .max(DateTime.local().plus({ days: 1 }), 'La data non può essere successiva alla data odierna')
    .typeError('Data non valida'),
  authority: Yup.string().nullable().required('Campo richiesto'),
  courtType: Yup.string().nullable().required('Campo richiesto'),
  isItalianAuthority: Yup.boolean(),
  italianAuthority: Yup.object().when('isItalianAuthority', {
    is: (isItalianAuthority: boolean) => !isItalianAuthority,
    then: Yup.object().shape({}).nullable().notRequired(),
    otherwise: Yup.object()
      .nullable()
      .shape({
        municipalityId: Yup.string().nullable().required('Campo richiesto'),
        municipalityDescription: Yup.string().nullable().required('Campo richiesto'),
        province: Yup.string().nullable().required('Campo richiesto'),
        provinceCode: Yup.string().nullable().required('Campo richiesto'),
        location: Yup.string().nullable(),
      }),
  }),
  foreignAuthority: Yup.object().when('isItalianAuthority', {
    is: (isItalianAuthority: boolean) => isItalianAuthority,
    then: Yup.object().shape({}).nullable().notRequired(),
    otherwise: Yup.object()
      .nullable()
      .shape({
        stateId: Yup.string().nullable().required('Campo richiesto'),
        stateDescription: Yup.string().nullable().required('Campo richiesto'),
        municipalityId: Yup.string().nullable().required('Campo richiesto'),
        municipalityDescription: Yup.string().nullable().required('Campo richiesto'),
      }),
  }),
});

const subjectListFormSchema = Yup.object().shape({
  fiscalCodeHouseholder: Yup.string().nullable(),
  family: Yup.object().shape({}),
});

export const cancellationValidationSchema = Yup.object()
  .shape({
    changeControlDataForm: changeControlDataFormSchema,
    deathDataForm: deathDataFormSchema,
    judgmentDataForm: Yup.object()
      .shape({})
      .when('changeControlDataForm', {
        is: (value: ChangeControlDataForm) => value?.cancellationReason !== '1',
        then: judgmentDataFormSchema,
      }),
    // se intestatario e famiglia uguale a 2 non chiedere e muoiono entrambi (deathPartnerResident)
    subjectListForm: subjectListFormSchema,
  })
  .test('', '', (values) => {
    const castedValues = values as unknown as CancellationDueDeath;

    const errors: Array<Yup.ValidationError> = [];

    let marriedSubject: Generality | undefined | null;

    if (castedValues.subjectData?.matrimonio) {
      marriedSubject = castedValues.subjectData?.matrimonio?.coniuge;
    }

    if (castedValues.subjectData?.unioneCivile) {
      marriedSubject = castedValues.subjectData?.unioneCivile?.unitoCivilmente;
    }

    if (castedValues.showSubjectListSection) {
      // lunghezza di subjectListForm è due ed è coniugato
      // allora non chiedere validazione
      if (
        castedValues.subjectListForm?.members?.length === 2 &&
        castedValues.deathDataForm?.deathPartnerResident &&
        !(
          castedValues.subjectListForm.members?.filter(
            (el) =>
              el.generalita?.codiceFiscale?.codFiscale !==
                castedValues.subjectData?.generalita?.codiceFiscale?.codFiscale &&
              el.generalita?.codiceFiscale?.codFiscale !== marriedSubject?.codiceFiscale?.codFiscale
          ).length > 0
        )
      ) {
        return true;
      } else {
        if (!castedValues.subjectListForm?.fiscalCodeHouseholder) {
          errors.push(
            new Yup.ValidationError(
              'Campo obbligatorio',
              undefined,
              `subjectListForm.fiscalCodeHouseholder`
            )
          );
        }

        if (
          !Object.values(castedValues.subjectListForm?.family || {}).every((subject) =>
            Boolean(subject.relation)
          )
        ) {
          errors.push(
            new Yup.ValidationError('Mancano delle relazioni', undefined, `subjectListForm.family`)
          );
        }
      }
    }

    if (errors.length > 0) {
      return new Yup.ValidationError(errors);
    }
    return true;
  });
